import { useTranslation } from 'next-i18next';
import React, { ReactElement, useRef, useState } from 'react';
import { BsIcon } from '@hotelplan/core.basis.bs-icon';
import { AuthChannelType } from '@hotelplan/fdr.lib.ident.auth-config';
import { useAuthentication } from '@hotelplan/fdr.lib.ident.with-auth';
import {
  useIsomorphicLayoutEffect,
  useToggleState,
} from '@hotelplan/libs.hooks-react';
import { LoadMoreButton } from 'components/domain/recommendations/LoadMoreButton';
import { FdrCo2Emission } from './fdr-co2-emission';
import { FdrCo2FootprintDescription } from './fdr-co2-footprint-description';
import { FdrCo2FootprintModalDesktop } from './fdr-co2-footprint-modal-desktop';
import {
  FdrCO2CloseButtonWrapper,
  FdrCO2DescriptionTextSub,
  FdrCO2EmissionWrapperDesktop,
  FdrCO2FootprintBodyInnerDesktop,
  FdrCO2FootprintBodyWrapperDesktop,
  FdrCO2FootprintContentWrapper,
  FdrCO2FootprintHeaderDesktopWrapper,
  FdrCO2FootprintMoreInfoDesktop,
  FdrCO2FootprintTotalEmissionWrapper,
  FdrCO2FooterDesktopWrapper,
  FdrCO2LoadMoreButtonWrapperDesktop,
} from './fdr-co2-footprint.styles';
import {
  IFdrCo2FootprintBodyProps,
  IFdrCo2FootprintHeaderProps,
} from './fdr-co2-footprint.types';
import { getTotalEmission } from './fdr-co2-footprint.utils';

export function FdrCo2FootprintHeaderDesktop({
  totalEmission,
  isOpened,
  onOpen,
}: IFdrCo2FootprintHeaderProps): ReactElement {
  const [t] = useTranslation('pdp');

  return (
    <FdrCO2FootprintHeaderDesktopWrapper>
      <div>
        <h2> {t('pdp:co2Footprint.title')}</h2>
        <FdrCO2LoadMoreButtonWrapperDesktop isShow={!isOpened}>
          <LoadMoreButton onClick={onOpen}>
            {t('pdp:co2Footprint.loadMore')}
          </LoadMoreButton>
        </FdrCO2LoadMoreButtonWrapperDesktop>
      </div>
      <FdrCO2FootprintTotalEmissionWrapper isShow={!isOpened} onClick={onOpen}>
        <BsIcon name="total-emission" />
        <div
          dangerouslySetInnerHTML={{
            __html: t('pdp:co2Footprint.totalEmission', {
              emission: getTotalEmission(totalEmission),
            }),
          }}
        />
      </FdrCO2FootprintTotalEmissionWrapper>
    </FdrCO2FootprintHeaderDesktopWrapper>
  );
}

export function FdrCo2FootprintBodyDesktop({
  isOpened,
  onClose,
  totalEmission,
  requestButton,
  ...otherEmissions
}: IFdrCo2FootprintBodyProps): ReactElement {
  const [t] = useTranslation('pdp');

  const [bodyHeight, setBodyHeight] = useState(0);
  const [isShowModal, openModal, closeModal] = useToggleState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const { channelType } = useAuthentication();
  const isB2b = channelType === AuthChannelType.B2B;

  useIsomorphicLayoutEffect(() => {
    setBodyHeight(bodyRef?.current.clientHeight || 0);
  });

  return (
    <FdrCO2FootprintBodyWrapperDesktop
      isOpen={isOpened}
      bodyHeight={bodyHeight}
    >
      <FdrCO2FootprintBodyInnerDesktop ref={bodyRef}>
        <FdrCO2FootprintContentWrapper>
          <div>
            <FdrCo2FootprintDescription totalEmission={totalEmission} />
          </div>
          <FdrCO2EmissionWrapperDesktop>
            <FdrCo2Emission {...otherEmissions} />
          </FdrCO2EmissionWrapperDesktop>
        </FdrCO2FootprintContentWrapper>
        <FdrCO2DescriptionTextSub
          dangerouslySetInnerHTML={{
            __html: t(
              `pdp:co2Footprint.${
                isB2b ? 'descriptionTextSubB2b' : 'descriptionTextSub'
              }`
            ),
          }}
        />
        <FdrCO2FooterDesktopWrapper>
          <FdrCO2CloseButtonWrapper>
            <LoadMoreButton onClick={onClose}>
              {t('pdp:co2Footprint.closeBody')}
            </LoadMoreButton>
          </FdrCO2CloseButtonWrapper>
          <FdrCO2FootprintMoreInfoDesktop onClick={openModal}>
            <BsIcon name="info-small-icon" />
            {t('pdp:co2Footprint.moreInfoTitle')}
          </FdrCO2FootprintMoreInfoDesktop>
        </FdrCO2FooterDesktopWrapper>
      </FdrCO2FootprintBodyInnerDesktop>
      {isShowModal ? (
        <FdrCo2FootprintModalDesktop
          onClose={closeModal}
          isOpened={isShowModal}
          requestButton={requestButton}
        />
      ) : null}
    </FdrCO2FootprintBodyWrapperDesktop>
  );
}
